/** @format */

import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  Card,
  Button,
  Table,
  TableColumn,
  Pagination,
  Icon,
  Tooltip,
  Dialog,
  Checkbox,
  Drawer,
  Descriptions,
  DescriptionItem,
  Tag
} from '@king-design/react'
import MonitorCrumb from 'src/components/MonitorCrumb'
import { MonitorSearch } from 'src/components'
import { useParams } from 'react-router'
import dayjs from 'dayjs'
import { ActionTypeEnum, TriggerTypeEnum } from '../container'
import { get } from 'lodash'
import { useAsyncFn } from 'react-use'
import ReactJson from 'react-json-view'
import Highlighter from 'react-highlight-words'
import { getTriggerRecordList, getTriggerRecordDetail } from 'src/api/ApiEventOpsTrigger'
import { PageProps } from 'src/utils/interface'

interface SearchProps extends PageProps {
  Result: string
}
const initSearch: SearchProps = {
  Page: 1,
  PageSize: 10,
  Result: ''
}

const allColumns = [
  {
    label: '编号',
    value: 'ID'
  },
  {
    label: '规则名称',
    value: 'Name'
  },
  {
    label: '触发源',
    value: 'TriggerType'
  },
  {
    label: '动作类型',
    value: 'ActionType'
  },
  {
    label: '开始时间',
    value: 'StartedAt'
  },
  {
    label: '结束时间',
    value: 'FinishedAt'
  },
  {
    label: '状态',
    value: 'Status'
  },
  {
    label: '输入参数',
    value: 'Original'
  },
  {
    label: '执行结果',
    value: 'ResultMsg'
  }
]

type ColumnValue = typeof allColumns[number]['value']

export const getStatus = (status: number) => {
  switch (status) {
    case 2:
      return (
        <Tag border="solid" type="success">
          <Icon color="success" className="k-icon-check" />
          <span style={{ marginLeft: 2 }}>执行成功</span>
        </Tag>
      )
    case 1:
      return (
        <Tag border="solid" type="primary">
          <Icon color="primary" className="k-icon-refresh" />
          <span style={{ marginLeft: 2 }}>执行中</span>
        </Tag>
      )
    case -1:
      return (
        <Tag border="solid" type="danger">
          <Icon color="danger" className="k-icon-close" />
          <span style={{ marginLeft: 2 }}>触发失败</span>
        </Tag>
      )
    case -2:
      return (
        <Tag border="solid" type="danger">
          <Icon color="danger" className="k-icon-close" />
          <span style={{ marginLeft: 2 }}>执行失败</span>
        </Tag>
      )
  }
}

const TriggerRecord: React.FC<{ className?: string }> = ({ className }) => {
  const storeCheckedColumns = localStorage.getItem('trigger-record-checked-columns')?.split(',')
  const initCheckedColumns: ColumnValue[] = storeCheckedColumns ?? [
    'ID',
    'Name',
    'TriggerType',
    'ActionType',
    'started_at',
    'FinishedAt',
    'Status',
    'ResultMsg'
  ]
  const params = useParams()
  const sign = get(params, 'sign')
  const ID = get(params, 'ID')
  const [search, setSearch] = useState<SearchProps>(initSearch)
  // 筛选列
  const [columnSetting, setColumnSetting] = useState<boolean>(false)
  const [checkedColumns, setCheckedColumns] = useState<ColumnValue[]>(initCheckedColumns)
  const [checkedColumnValues, setCheckedColumnValues] = useState<ColumnValue[]>(initCheckedColumns)
  // 详情
  const [detailDrawer, setDetailDrawer] = useState<boolean>(false)
  const [detailRecord, setDetailRecord] = useState(undefined)

  const [{ value: recordList }, fetchRecordList] = useAsyncFn(async () => {
    if (sign) {
      const result = await getTriggerRecordList({
        Sign: ID ? undefined : sign,
        TriggerID: ID ? parseInt(ID) : undefined,
        Limit: search.PageSize,
        Offset: (search.Page - 1) * search.PageSize,
        Result: search.Result
      })
      return result
    } else {
      setSearch(initSearch)
      return {
        Info: [],
        Total: 0
      }
    }
  }, [sign, search, ID])

  useEffect(() => {
    fetchRecordList()
  }, [sign, search, ID])

  const renderActionType = (actionType: string, actionRecordID: string) => {
    if (actionType === 'location') {
      if (actionRecordID) {
        return (
          <Tooltip content="点击查看详情">
            <a
              onClick={() => {
                window.open(`/#!/monitor/eventops/record/detail/${actionRecordID}`, '_blank')
              }}
            >
              自动化场景
            </a>
          </Tooltip>
        )
      } else {
        return <span className="disabled-text">自动化场景</span>
      }
    } else {
      return ActionTypeEnum[actionType] || '-'
    }
  }

  return (
    <div id="trigger-record-page" className={className}>
      <MonitorCrumb
        breadArr={[
          { title: '自动化运维' },
          { title: '触发器', route: '/monitor/eventops/trigger' },
          { title: '触发记录' }
        ]}
      />
      <Card style={{ marginBottom: 16 }}>
        <div className="search_row">
          <div className="search_group">
            <MonitorSearch
              placeholder="执行结果搜索"
              onChange={value => {
                setSearch({
                  ...search,
                  Page: 1,
                  Result: value
                })
              }}
            />
          </div>
          <div className="ksc-space">
            <Button
              icon
              onClick={() => {
                setColumnSetting(true)
              }}
            >
              <Icon className="k-icon-settings" />
            </Button>
            <Button
              icon
              onClick={() => {
                fetchRecordList()
              }}
            >
              <Icon className="ion-refresh" />
            </Button>
          </div>
        </div>
        <Table
          data={recordList?.Info || []}
          animation={false}
          rowKey={record => record.ID}
          checkType="none"
        >
          <TableColumn
            key="ID"
            title="编号"
            width="80"
            fixed="left"
            hidden={!checkedColumns.includes('ID')}
          />
          <TableColumn
            key="Name"
            title="规则名称"
            width="200"
            fixed="left"
            hidden={!checkedColumns.includes('Name')}
          />
          <TableColumn
            key="TriggerType"
            title="触发源"
            width="80"
            hidden={!checkedColumns.includes('TriggerType')}
            slotTemplate={([{ TriggerType }]) => {
              return TriggerTypeEnum[TriggerType] || '-'
            }}
          />
          <TableColumn
            key="ActionType"
            title="触发动作"
            width="100"
            hidden={!checkedColumns.includes('ActionType')}
            slotTemplate={([{ ActionType, ActionRecordID }]) => {
              return renderActionType(ActionType, ActionRecordID)
            }}
          />
          <TableColumn
            key="StartedAt"
            title="开始时间"
            width="100"
            hidden={!checkedColumns.includes('StartedAt')}
            slotTemplate={([{ StartedAt }]) => {
              return StartedAt ? dayjs(StartedAt).format('YYYY-MM-DD HH:mm:ss') : '-'
            }}
          />
          <TableColumn
            key="FinishedAt"
            title="结束时间"
            width="100"
            hidden={!checkedColumns.includes('FinishedAt')}
            slotTemplate={([{ FinishedAt }]) => {
              return FinishedAt ? dayjs(FinishedAt).format('YYYY-MM-DD HH:mm:ss') : '-'
            }}
          />
          <TableColumn
            key="Status"
            title="状态"
            width="100"
            hidden={!checkedColumns.includes('Status')}
            slotTemplate={([{ Status }]) => {
              return getStatus(Status)
            }}
          />
          <TableColumn
            key="Original"
            width="300"
            title="输入参数"
            hidden={!checkedColumns.includes('Original')}
            slotTemplate={([{ Original }]) => {
              return (
                <ReactJson
                  src={Original ?? {}}
                  name={false}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  quotesOnKeys={false}
                  enableClipboard={false}
                  collapsed={1}
                />
              )
            }}
          />
          <TableColumn
            key="ResultMsg"
            width="200"
            title="执行结果"
            hidden={!checkedColumns.includes('ResultMsg')}
            slotTemplate={([{ ResultMsg }]) => {
              return (
                <Highlighter
                  highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                  searchWords={[search.Result]}
                  autoEscape
                  textToHighlight={Array.isArray(ResultMsg) ? ResultMsg.join(',') : ResultMsg}
                />
              )
            }}
          />
          <TableColumn
            key="Operations"
            title="操作"
            fixed="right"
            width="80"
            slotTemplate={([record]) => {
              return (
                <a
                  onClick={async () => {
                    const result = await getTriggerRecordDetail({
                      ID: record.ID
                    })
                    setDetailDrawer(true)
                    setDetailRecord(result)
                  }}
                >
                  详情
                </a>
              )
            }}
          />
        </Table>
        <Pagination
          value={search.Page}
          limit={search.PageSize}
          total={recordList?.Total || 0}
          size="small"
          onChangeValue={value => {
            setSearch({
              ...search,
              Page: value
            })
          }}
          onChangeLimit={limit => {
            setSearch({
              ...search,
              PageSize: limit
            })
          }}
        />
      </Card>
      <Dialog
        value={columnSetting}
        width={600}
        title="自定义表格显示项"
        container={() => document.querySelector('#trigger-record-page')}
        ok={() => {
          setCheckedColumns(checkedColumnValues)
          setColumnSetting(false)
          localStorage.setItem('trigger-record-checked-columns', checkedColumnValues.join(','))
        }}
        onChangeValue={show => {
          setColumnSetting(show)
          if (!show) {
            setCheckedColumns(initCheckedColumns)
            setCheckedColumnValues(initCheckedColumns)
          }
        }}
      >
        <div className="ksc-space">
          {allColumns.map(item => {
            return (
              <Checkbox
                key={item.value}
                name="column"
                trueValue={item.value}
                value={checkedColumnValues}
                onChangeValue={value => {
                  setCheckedColumnValues(value)
                }}
              >
                {item.label}
              </Checkbox>
            )
          })}
        </div>
      </Dialog>
      <Drawer
        value={detailDrawer}
        width={600}
        title="详细信息"
        container={() => document.querySelector('#trigger-record-page')}
        onChangeValue={value => {
          setDetailDrawer(value)
        }}
        slotFooter={<Button onClick={() => setDetailDrawer(false)}>关闭</Button>}
      >
        <Descriptions columns={1}>
          <DescriptionItem label="规则名称">{detailRecord?.Name || '-'}</DescriptionItem>
          <DescriptionItem label="触发源">
            {detailRecord?.TriggerType ? TriggerTypeEnum[detailRecord?.TriggerType] : '-'}
          </DescriptionItem>
          <DescriptionItem label="触发动作">
            {detailRecord?.ActionType
              ? renderActionType(detailRecord?.ActionType, detailRecord?.ActionRecordID)
              : '-'}
          </DescriptionItem>
          <DescriptionItem label="数据源">
            {typeof detailRecord?.Original === 'string' ? (
              detailRecord?.Original
            ) : (
              <ReactJson
                src={detailRecord?.Original ?? {}}
                name={false}
                displayDataTypes={false}
                displayObjectSize={false}
                quotesOnKeys={false}
                enableClipboard={false}
                collapsed={1}
              />
            )}
          </DescriptionItem>
          <DescriptionItem label="参数">
            {typeof detailRecord?.ActionParams === 'string' ? (
              detailRecord?.ActionParams
            ) : (
              <ReactJson
                src={detailRecord?.ActionParams ?? {}}
                name={false}
                displayDataTypes={false}
                displayObjectSize={false}
                quotesOnKeys={false}
                enableClipboard={false}
                collapsed={1}
              />
            )}
          </DescriptionItem>
          <DescriptionItem label="状态">{getStatus(detailRecord?.Status) || '-'}</DescriptionItem>
          <DescriptionItem label="执行结果">{detailRecord?.ResultMsg || '-'}</DescriptionItem>
          <DescriptionItem label="执行数据">
            {typeof detailRecord?.ResultData === 'string' ? (
              detailRecord?.ResultData
            ) : (
              <ReactJson
                src={detailRecord?.ResultData ?? {}}
                name={false}
                displayDataTypes={false}
                displayObjectSize={false}
                quotesOnKeys={false}
                enableClipboard={false}
                collapsed={1}
              />
            )}
          </DescriptionItem>
          <DescriptionItem label="开始时间">
            {detailRecord?.StartedAt
              ? dayjs(detailRecord?.StartedAt).format('YYYY-MM-DD HH:mm:ss')
              : '-'}
          </DescriptionItem>
          <DescriptionItem label="结束时间">
            {detailRecord?.FinishedAt
              ? dayjs(detailRecord?.FinishedAt).format('YYYY-MM-DD HH:mm:ss')
              : '-'}
          </DescriptionItem>
        </Descriptions>
      </Drawer>
    </div>
  )
}

export default styled(TriggerRecord)`
  .search_row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  .k-pagination {
    margin-top: 16px;
    text-align: center;
  }
  .k-dialog-body {
    .k-checkbox {
      margin-bottom: 8px;
    }
  }
  .k-description-centent {
    max-width: 480px;
    .k-ellipsis {
      width: 480px;
    }
  }
`
